import { createFileRoute } from "@tanstack/react-router";
import { CircleCheck } from "lucide-react";

import { PageDescription } from "#/components/PageDescription";
import { PageEyeBrow } from "#/components/PageEyeBrow";
import { PageHeader } from "#/components/PageHeader";
import { PageIntro } from "#/components/PageIntro";
import { Alert, AlertDescription, AlertTitle } from "#/components/ui/alert";
import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";
import { premiumSearchSchema } from "#/lib/routeSearchSchemas";
import { EarlyBirdPromoCard } from "#/routes/-index.earlyBirdPromo";
import { PremiumOfferCard } from "#/routes/-premium.offerCard";
import { PromoCardBackground } from "#/routes/-premium.promoCardShell";
import { loadPremiumPage } from "#/server/loadPremiumPage";

export const Route = createFileRoute("/premium")({
  head: () => documentHead(DOCUMENT_TITLES.premium),
  validateSearch: (search) => premiumSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ checkoutSuccess: search.checkout === "success" }),
  loader: async ({ deps }) => loadPremiumPage({ data: { checkoutSuccess: deps.checkoutSuccess } }),
  component: PremiumPage,
});

function PremiumCheckoutSuccessAlert() {
  return (
    <div className="relative mx-auto mt-8 max-w-lg overflow-hidden rounded-lg">
      <PromoCardBackground />
      <Alert className="relative border-premium/60 bg-card text-foreground shadow-2xl shadow-premium/20 ring-2 ring-premium/70">
        <CircleCheck className="text-foreground" aria-hidden />
        <AlertTitle>Thanks for subscribing</AlertTitle>
        <AlertDescription>
          Premium access activates shortly after payment is confirmed.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function PremiumPage() {
  const { checkout } = Route.useSearch();
  const { hasBillingRecord, showEarlyBirdPromo } = Route.useLoaderData();
  const checkoutSuccess = checkout === "success";

  return (
    <main className="mx-auto w-full max-w-[1080px] px-4 py-10">
      <PageIntro>
        <PageEyeBrow>Unlock Everything</PageEyeBrow>
        <PageHeader>Premium</PageHeader>
        <PageDescription>
          Premium gives you access to everything Daily Wars has to offer.
        </PageDescription>
      </PageIntro>

      {checkoutSuccess ? <PremiumCheckoutSuccessAlert /> : null}

      {!checkoutSuccess && showEarlyBirdPromo ? (
        <div className="mx-auto mt-10 w-full max-w-2xl">
          <EarlyBirdPromoCard />
        </div>
      ) : null}

      {!checkoutSuccess && !showEarlyBirdPromo ? (
        <div className="mt-10 flex justify-center">
          <PremiumOfferCard
            manageBilling={
              hasBillingRecord ? (
                <p className="text-center text-sm text-muted-foreground">
                  Already subscribed?{" "}
                  <a
                    href="/api/portal"
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                  >
                    Manage billing
                  </a>
                </p>
              ) : null
            }
          />
        </div>
      ) : null}
    </main>
  );
}
